import { Seo } from '@frontend/shared/web';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { MultiSelect } from '../../components/common/MultiSelect';
import { IncidentDonut } from '../../components/dashboard/IncidentDonut';
import { KpiGrid } from '../../components/dashboard/KpiGrid';
import { NoteClientCard } from '../../components/dashboard/NoteClientCard';
import { TimeSeriesChart } from '../../components/dashboard/TimeSeriesChart';
import { useFilterStore } from '../../components/filters/filterStore';
import { GlobalFilters } from '../../components/filters/GlobalFilters';
import { Header } from '../../components/layout/Header';
import { RidesPagination } from '../../components/rides/RidesPagination';
import { RidesTable } from '../../components/rides/RidesTable';
import { TRIP_STATUS_FILTER_LABELS, TRIP_STATUS_FILTERS } from '../../components/rides/trip';
import { FEATURES } from '../../constants';
import { useAccount } from '../../contexts/AccountContext';
import { useDebouncedValue } from '../../lib/useDebouncedValue';
import { useRides } from '../../services/api/hooks/useRides';
import type { TripStatusFilter } from '../../types/extranet-api.types';

const RIDES_PAGE_SIZE = 25;

const STATUS_OPTIONS = TRIP_STATUS_FILTERS.map(value => ({ value, label: TRIP_STATUS_FILTER_LABELS[value] }));

export const DashboardRoute = () => {
  const { can } = useAccount();
  const { scopes, dateRange } = useFilterStore();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<TripStatusFilter[]>([]);
  const [firstNameDraft, setFirstNameDraft] = useState('');
  const [lastNameDraft, setLastNameDraft] = useState('');
  const passengerFirstName = useDebouncedValue(firstNameDraft.trim());
  const passengerLastName = useDebouncedValue(lastNameDraft.trim());

  // Tout changement de filtre local ramène à la première page.
  useEffect(() => {
    setPage(1);
  }, [passengerFirstName, passengerLastName, status, scopes, dateRange]);

  const canCreateIncident = FEATURES.createIncident && can('incidents');
  const showPerformance = can('performance');
  const showIncidents = can('incidents');

  const ridesQuery = useRides({
    scopes,
    dateRange,
    page,
    limit: RIDES_PAGE_SIZE,
    status,
    passengerFirstName,
    passengerLastName,
  });

  const rides = ridesQuery.data?.items ?? [];
  const ridesTotal = ridesQuery.data?.totalItems ?? rides.length;

  return (
    <>
      <Seo title="Tableau de bord — Portail extranet" />
      <Header title="Tableau de bord" subtitle="Vue synthétique de votre activité" />

      <div className="flex-1 overflow-y-auto">
        <div className="px-8 py-6 flex flex-col gap-6">
          <GlobalFilters />

          {FEATURES.dashboardNotes ? <NoteClientCard /> : null}

          {showPerformance ? <KpiGrid /> : null}

          {showIncidents || showPerformance ? (
            <section
              className={['grid gap-4 grid-cols-1', showIncidents && showPerformance ? 'lg:grid-cols-2' : ''].join(' ')}
            >
              {showIncidents ? <IncidentDonut /> : null}
              {showPerformance ? <TimeSeriesChart /> : null}
            </section>
          ) : null}

          <section className="flex flex-col gap-3">
            <header className="flex items-end justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-sm font-medium text-zinc-900">Courses</h2>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Toutes les courses du périmètre filtré, par pages de {RIDES_PAGE_SIZE}.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <MultiSelect
                  label="Statut"
                  placeholder="Tous"
                  options={STATUS_OPTIONS}
                  value={status}
                  onChange={next => setStatus(next as TripStatusFilter[])}
                />
                <PassengerNameInput
                  id="rides-passenger-first-name"
                  value={firstNameDraft}
                  onChange={setFirstNameDraft}
                  placeholder="Prénom passager"
                />
                <PassengerNameInput
                  id="rides-passenger-last-name"
                  value={lastNameDraft}
                  onChange={setLastNameDraft}
                  placeholder="Nom passager"
                />
              </div>
            </header>

            <RidesTable
              items={rides}
              total={ridesTotal}
              isLoading={ridesQuery.isLoading}
              isFetching={ridesQuery.isFetching}
              isError={ridesQuery.isError}
              error={ridesQuery.error}
              onRowAction={
                canCreateIncident
                  ? trip => {
                      navigate(`/incidents?createForRide=${encodeURIComponent(trip.id)}`);
                    }
                  : undefined
              }
              actionLabel={canCreateIncident ? 'Signaler une réclamation' : undefined}
            />

            <RidesPagination
              page={page}
              limit={RIDES_PAGE_SIZE}
              pageSize={rides.length}
              total={ridesTotal}
              onPageChange={setPage}
            />
          </section>
        </div>
      </div>
    </>
  );
};

const PassengerNameInput = ({
  id,
  value,
  onChange,
  placeholder,
}: {
  id: string;
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
}) => (
  <input
    id={id}
    type="text"
    value={value}
    onChange={e => onChange(e.target.value)}
    placeholder={placeholder}
    aria-label={placeholder}
    className="w-40 h-8 px-3 text-sm bg-white border border-zinc-300 rounded-md text-zinc-900 placeholder:text-zinc-400 focus:outline-none focus:border-zinc-400 focus:ring-2 focus:ring-zinc-900/10 transition-colors"
  />
);
